/**
 * Where the app sends the user when the session is lost mid-use (a backend
 * 401, or silent token renewal giving up).
 *
 * Deliberately the app's own /login page rather than Auth0 directly. Auth0
 * ties each /authorize redirect to a short-lived transaction; opening its
 * page on the app's initiative — from a background request, in a tab the
 * user is not looking at — leaves that transaction to expire or be displaced
 * by another tab's redirect, and the identifier form then fails with
 * "invalid_request: we couldn't find your session". Landing in-app means the
 * Auth0 transaction is created by the user's click, seconds before use.
 */
export const SESSION_ENDED_REASON = 'session-ended';

export function sessionEndedLoginPath(
    pathname: string,
    search: string
): string {
    const params = new URLSearchParams({ reason: SESSION_ENDED_REASON });
    const returnTo = `${pathname}${search}`;
    // The login page itself is never worth returning to.
    if (returnTo && !returnTo.startsWith('/login')) {
        params.set('returnTo', returnTo);
    }
    return `/login?${params.toString()}`;
}

export function parseLoginParams(search: string): {
    sessionEnded: boolean;
    returnTo: string | undefined;
} {
    const params = new URLSearchParams(search);
    const returnTo = params.get('returnTo') ?? undefined;
    return {
        sessionEnded: params.get('reason') === SESSION_ENDED_REASON,
        // Only same-app paths: a full URL here would be an open redirect.
        returnTo:
            returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')
                ? returnTo
                : undefined,
    };
}
